import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, MapPin, Calendar, X, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SearchModal = ({ open, onClose, onSearch, initialFilters = {} }) => {
  const [step, setStep] = useState('main'); // main, location, dates
  const [searchText, setSearchText] = useState(initialFilters.search || '');
  const [location, setLocation] = useState(initialFilters.location || '');
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || '');
  const [suggestions, setSuggestions] = useState({ locations: [], activities: [] });
  const [allCities, setAllCities] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch all cities on mount
  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/search/suggestions?q=`).then(res => {
        setAllCities(res.data.locations || []);
      }).catch(() => {});
      // Focus search input after animation
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [open]);

  // Focus location input when switching to location step
  useEffect(() => {
    if (step === 'location') {
      setTimeout(() => locationInputRef.current?.focus(), 100);
    }
  }, [step]);

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions({ locations: allCities, activities: [] });
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/api/search/suggestions?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data);
    } catch {
      setSuggestions({ locations: [], activities: [] });
    } finally {
      setLoadingSuggestions(false);
    }
  }, [allCities]);

  const handleSearchTextChange = (val) => {
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleLocationChange = (val) => {
    setLocation(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSubmit = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    onSearch({
      search: searchText.trim(),
      location: location.trim(),
      dateFrom,
      dateTo,
    });
  };

  const handleClearAll = () => {
    setSearchText('');
    setLocation('');
    setDateFrom('');
    setDateTo('');
    setStep('main');
  };

  const hasFilters = searchText || location || dateFrom || dateTo;

  if (!open) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[60] bg-white md:bg-black/40"
      data-testid="search-modal"
    >
      {/* Desktop backdrop */}
      <div className="hidden md:block fixed inset-0 bg-black/40" onClick={onClose} />
      
      {/* Modal content */}
      <div className="relative md:max-w-xl md:mx-auto md:mt-20 md:rounded-2xl md:bg-white md:shadow-2xl md:max-h-[80vh] overflow-hidden h-full md:h-auto flex flex-col bg-white overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="search-modal-close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <span className="font-medium text-sm text-gray-500">Buscar experiencias</span>
          <div className="w-9" /> {/* spacer for centering */}
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto">
          {step === 'main' && (
            <div className="p-4 space-y-3">
              {/* Search text input */}
              <div
                className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-text"
                onClick={() => searchInputRef.current?.focus()}
              >
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Qué buscas
                </label>
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchText}
                    onChange={(e) => handleSearchTextChange(e.target.value)}
                    placeholder="Talleres, cocina, yoga..."
                    className="flex-1 text-[16px] bg-transparent border-none outline-none placeholder:text-gray-300"
                    data-testid="search-modal-text-input"
                  />
                  {searchText && (
                    <button onClick={() => { setSearchText(''); setSuggestions({ locations: allCities, activities: [] }); }} className="p-1 rounded-full hover:bg-gray-100">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Text suggestions from search */}
              {searchText.length >= 2 && suggestions.activities?.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                  <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Sugerencias</p>
                  {suggestions.activities.map(a => (
                    <button
                      key={a.activity_id}
                      onClick={() => { setSearchText(a.title); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Search className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      <span className="text-sm text-gray-600 truncate">{a.title}</span>
                      <span className="text-xs text-gray-300 flex-shrink-0 ml-auto">{a.city}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Location filter */}
              <button
                onClick={() => { setStep('location'); fetchSuggestions(location || ''); }}
                className="w-full bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-left"
                data-testid="search-modal-location-btn"
              >
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Ubicación
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-4 h-4 flex-shrink-0 ${location ? 'text-primary' : 'text-gray-400'}`} />
                    <span className={`text-sm ${location ? 'text-gray-700 font-medium' : 'text-gray-300'}`}>
                      {location || 'Estado, ciudad o colonia'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>

              {/* Date filter */}
              <button
                onClick={() => setStep('dates')}
                className="w-full bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-left"
                data-testid="search-modal-dates-btn"
              >
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Fechas
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className={`w-4 h-4 flex-shrink-0 ${dateFrom ? 'text-primary' : 'text-gray-400'}`} />
                    <span className={`text-sm ${dateFrom ? 'text-gray-700 font-medium' : 'text-gray-300'}`}>
                      {dateFrom && dateTo 
                        ? `${new Date(dateFrom + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${new Date(dateTo + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
                        : dateFrom 
                          ? `Desde ${new Date(dateFrom + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
                          : 'Cualquier fecha'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
            </div>
          )}

          {step === 'location' && (
            <div className="p-4">
              <button onClick={() => setStep('main')} className="flex items-center gap-1 text-sm text-gray-400 mb-4 hover:text-gray-600">
                <ChevronRight className="w-4 h-4 rotate-180" /> Volver
              </button>
              <h2 className="text-xl font-bold text-gray-800 mb-4">¿Dónde?</h2>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={locationInputRef}
                  type="text"
                  value={location}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  placeholder="Busca una ciudad o estado..."
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-[16px] outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                  data-testid="search-modal-location-input"
                />
                {location && (
                  <button onClick={() => { setLocation(''); fetchSuggestions(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>

              {/* City suggestions */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {location ? 'Resultados' : 'Ciudades disponibles'}
                </p>
                {(location ? suggestions.locations : allCities).length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No se encontraron ubicaciones</p>
                ) : (
                  (location ? suggestions.locations : allCities).map(city => (
                    <button
                      key={city}
                      onClick={() => { setLocation(city); setStep('main'); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-gray-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{city}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {step === 'dates' && (
            <div className="p-4 overflow-x-hidden">
              <button onClick={() => setStep('main')} className="flex items-center gap-1 text-sm text-gray-400 mb-4 hover:text-gray-600">
                <ChevronRight className="w-4 h-4 rotate-180" /> Volver
              </button>
              <h2 className="text-xl font-bold text-gray-800 mb-4">¿Cuándo?</h2>
              <div className="space-y-4 overflow-hidden">
                <div className="overflow-hidden">
                  <label className="text-sm text-gray-500 mb-1 block">Desde</label>
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="block w-full h-12 px-4 text-base text-center outline-none focus:ring-0 border-none bg-transparent leading-[3rem]"
                      data-testid="search-modal-date-from"
                    />
                  </div>
                </div>
                <div className="overflow-hidden">
                  <label className="text-sm text-gray-500 mb-1 block">Hasta</label>
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || new Date().toISOString().split('T')[0]}
                      className="block w-full h-12 px-4 text-base text-center outline-none focus:ring-0 border-none bg-transparent leading-[3rem]"
                      data-testid="search-modal-date-to"
                    />
                  </div>
                </div>
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-sm text-primary hover:underline"
                  >
                    Limpiar fechas
                  </button>
                )}
                <Button
                  onClick={() => setStep('main')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-xl h-12"
                >
                  Aplicar fechas
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - search button */}
        {step === 'main' && (
          <div className="flex items-center justify-between px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px)+60px)] md:pb-3 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={handleClearAll}
              className={`text-sm font-medium ${hasFilters ? 'text-gray-600 underline' : 'text-gray-300'}`}
              disabled={!hasFilters}
            >
              Limpiar todo
            </button>
            <Button
              onClick={(e) => handleSubmit(e)}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl px-6 h-11 flex items-center gap-2"
              data-testid="search-modal-submit"
            >
              <Search className="w-4 h-4" />
              Buscar
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;
